/**
 * Hook to get tenantId from context with validation
 *
 * Provides a single source of truth for tenantId access across the app
 * with consistent error handling when tenantId is not available.
 *
 * IMPORTANT: Only use this hook in components protected by AuthGuard (Layer 1).
 * AuthGuard ensures the user is authenticated before rendering children,
 * so tenantId is guaranteed to be available.
 */

import {useAppContext} from '@/matic/providers/AppProvider';

/**
 * Get tenantId from app context with validation
 *
 * @throws Error if tenantId is not available in context
 * @returns tenantId string
 *
 * NOTE: This hook is strict and will throw if tenantId is missing.
 * It relies on AuthGuard (Layer 1) to ensure authentication is complete.
 * Safe to use in protected routes because AuthGuard guarantees auth.
 */
export function useRequiredTenantId(): string {
  const {tenantId} = useAppContext();

  if (!tenantId) {
    throw new Error('TenantId not available in context');
  }

  return tenantId;
}
